import { api_fetch, api_fetch_throws } from "./client"

export interface Conversation {
  id: string
  org_id: string
  forecast_version_id?: string
  brand_file_id?: string
  context_forecast_version_ids?: string[]
  created_at: string
  updated_at: string
}

export interface Message {
  id: string
  conversation_id: string
  role: "user" | "assistant"
  content: string
  created_at: string
}

export interface CreateConversationRequest {
  forecast_version_id?: string
  brand_file_id?: string
  context_forecast_version_ids?: string[]
  force_new?: boolean
}

export interface SendMessageRequest {
  content: string
}

export interface ForecastConfirmationAction {
  action_type: string
  label: string
  params: Record<string, unknown>
  preview?: ForecastActionPreview
}

export interface ForecastActionPreview {
  action?: string
  version_id?: string
  edit_count?: number
  before_volume_dollars?: string | number
  after_volume_dollars?: string | number
  volume_delta?: string | number
  warnings?: string[]
  edits?: ForecastEditPreview[]
  source_version_name?: string
  new_version_name?: string
  scenario_kind?: string
  /** `remove_products`: each distinct product removed everywhere, and what it was worth. */
  products?: ForecastRemovedProduct[]
  rows_removed?: number
  customers_affected?: number
  total_volume_dollars?: string | number
  total_opportunity_dollars?: string | number
  total_cases?: number
  total_units?: string | number
}

export interface ForecastRemovedProduct {
  name: string
  upc?: string | null
  rows: number
  customers: number
  volume_dollars: string | number
  first_period?: string | null
  last_period?: string | null
}

export interface ForecastEditPreview {
  path_key?: string
  row_name?: string
  period?: string
  source_kind?: string
  is_locked?: boolean
  before?: {
    volume_dollars?: string | number
    cases_sold?: number
    store_count?: number
  }
  after?: {
    volume_dollars?: string | number
    cases_sold?: number
    store_count?: number
  }
}

export interface ConfirmForecastActionResponse {
  result: unknown
  message: Message
}

export interface ForecastChatStreamCallbacks {
  onThinking: (message: string) => void
  onThinkingDelta: (content: string) => void
  onThinkingDone: () => void
  onToolCall: (toolName: string, args: unknown) => void
  onToolResult: (toolName: string, result: unknown, confirmationRequired: boolean, confirmationAction?: ForecastConfirmationAction) => void
  onTextDelta: (content: string) => void
  onText: (content: string) => void
  onDone: () => void
  onError: (error: string) => void
  // Called when the request is deliberately aborted (conversation switch / unmount) so the
  // caller's await settles instead of hanging forever.
  onAborted?: () => void
}

const API_BASE = "/forecast-chat/conversations"

export async function createConversation(
  request: CreateConversationRequest
): Promise<Conversation> {
  const result = await api_fetch<Conversation>(API_BASE, {
    method: "POST",
    body: request,
  })
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to create conversation: ${result.error.message}`)
  }
}

export async function listConversations(params?: {
  forecast_version_id?: string
  brand_file_id?: string
}): Promise<Conversation[]> {
  const queryParams = new URLSearchParams()
  if (params?.forecast_version_id) {
    queryParams.append("forecast_version_id", params.forecast_version_id)
  }
  if (params?.brand_file_id) {
    queryParams.append("brand_file_id", params.brand_file_id)
  }
  
  const url = queryParams.toString() 
    ? `${API_BASE}?${queryParams.toString()}`
    : API_BASE
    
  const result = await api_fetch<Conversation[]>(url, {
    method: "GET",
  })
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to list conversations: ${result.error.message}`)
  }
}

export async function sendMessage(
  conversationId: string,
  request: SendMessageRequest
): Promise<Message> {
  const result = await api_fetch<Message>(
    `${API_BASE}/${conversationId}/messages`,
    {
      method: "POST",
      body: request,
    }
  )
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to send message: ${result.error.message}`)
  }
}

export function sendMessageStream(
  conversationId: string,
  request: SendMessageRequest,
  callbacks: ForecastChatStreamCallbacks
): AbortController {
  const abortController = new AbortController()

  fetch(`/api${API_BASE}/${conversationId}/messages/stream`, {
    method: "POST",
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
    credentials: "same-origin",
    body: JSON.stringify(request),
    signal: abortController.signal,
  })
    .then(async (response: Response) => {
      if (!response.ok) {
        const text = await response.text()
        callbacks.onError(`Request failed: ${text}`)
        return
      }

      const reader = response.body?.getReader()
      if (!reader) {
        callbacks.onError("No response body")
        return
      }

      const decoder = new TextDecoder()
      let buffer = ""
      let doneReceived = false

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split("\n")
        buffer = lines.pop() || ""

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue

          const data = line.slice(6).trim()
          if (!data) continue

          try {
            const event = JSON.parse(data)
            switch (event.type) {
              case "thinking":
                callbacks.onThinking(event.message)
                break
              case "thinking_delta":
                callbacks.onThinkingDelta(event.content)
                break
              case "thinking_done":
                callbacks.onThinkingDone()
                break
              case "tool_call":
                callbacks.onToolCall(event.tool_name, event.args)
                break
              case "tool_result":
                callbacks.onToolResult(event.tool_name, event.result, event.confirmation_required, event.confirmation_action)
                break
              case "text_delta":
                callbacks.onTextDelta(event.content)
                break
              case "text":
                callbacks.onText(event.content)
                break
              case "done":
                doneReceived = true
                callbacks.onDone()
                break
              case "error":
                callbacks.onError(event.error)
                break
            }
          } catch (error) {
            console.error("Failed to parse forecast chat stream event:", data, error)
          }
        }
      }

      if (!doneReceived && !abortController.signal.aborted) {
        callbacks.onDone()
      }
    })
    .catch((error: Error) => {
      if (error.name === "AbortError") {
        callbacks.onAborted?.()
      } else {
        callbacks.onError(error.message)
      }
    })

  return abortController
}

export async function getMessages(conversationId: string): Promise<Message[]> {
  const result = await api_fetch<Message[]>(
    `${API_BASE}/${conversationId}/messages`,
    {
      method: "GET",
    }
  )
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to get messages: ${result.error.message}`)
  }
}

export async function confirmForecastAction(
  conversationId: string,
  action: ForecastConfirmationAction
): Promise<ConfirmForecastActionResponse> {
  const result = await api_fetch<ConfirmForecastActionResponse>(
    `${API_BASE}/${conversationId}/confirm`,
    {
      method: "POST",
      body: {
        action_type: action.action_type,
        params: action.params,
      },
    }
  )
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to confirm forecast action: ${result.error.message}`)
  }
}

